import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { JsonLd } from '../../utils/structuredData';
import { useInMarketingShell } from './MarketingContext';
import { TRUST_ROW } from '../Home/homeContent';
import '../AIEmployees/AIEmployeesLandingPage.css';
import './SecurityPage.css';

/**
 * SecurityPage: /security (Nitish, Sep 16 2026 01:44 ET: "it's just text, we can add
 * that text now"). The page the home and /ai-employees trust tiles link to, and the
 * page an IT or legal reader looks for before granting Gmail access.
 *
 * Every claim traces to org canon `security-claims` (Arjun, Sep 15 2026; amended by
 * Nitish Sep 16): dated text, Gmail + Google Analytics on our own Google client only,
 * "assessment completed" (never "letter issued" until the LOV PDF is in the repo),
 * never "certified". The CASA block carries the same badge slot as the trust row:
 * TAC Security's own mark appears only once TRUST_ROW.casa.badgeSrc points at the
 * file TAC delivers. Update this page at every annual reassessment.
 *
 * Theme: same face logic as StoryPage (logged-out marketing shell = adaptive via
 * MarketingLayout; logged-in honors the app theme). Registrations beyond this
 * component: App.js route, adaptivePaths.js, footer link (this build); react-snap,
 * seo.py sitemap, .md alternate, llms.txt, security.txt (Rhea, canon
 * agent-readable-web); LB URL map (Arjun, CEL-2720).
 */
const SecurityPage = () => {
  const { isDarkMode } = useTheme();
  const inMarketingShell = useInMarketingShell();
  const dark = inMarketingShell ? true : isDarkMode;
  const canonicalUrl = `${SEO_DEFAULTS.url}/security`;
  const title = 'Security at CellCog: Google verification and independent assessment';
  const description =
    'Google approved how CellCog connects to Gmail and Google Analytics in September 2026, after an App Defense Alliance authorized lab assessed our application security. What that means, and how we handle your data.';

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: title,
          description,
          url: canonicalUrl,
          isPartOf: { '@type': 'WebSite', name: SEO_DEFAULTS.siteName, url: SEO_DEFAULTS.url },
          dateModified: '2026-09-16',
        }}
      />

      <div className={`aie-landing security-page ${dark ? 'dark' : 'light'}`}>
        <div className="sec__inner">
          <header className="sec__head">
            <p className="sec__eyebrow">Security</p>
            <h1 className="sec__title aie-display">What Google and an independent lab reviewed</h1>
            <p className="sec__sub">
              Your AI employees connect to your Gmail and Google Analytics through CellCog.
              Before Google allows that at scale, two things have to happen. Both did in 2026,
              and both repeat every year.
            </p>
          </header>

          <section className="sec__block" aria-labelledby="sec-google">
            <h2 id="sec-google" className="sec__h2">{TRUST_ROW.gmail.title}</h2>
            <p className="sec__p">
              Google reviewed how CellCog asks for and handles access to Gmail and Google
              Analytics, and approved our OAuth app verification in <strong>September 2026</strong>.
              When you connect either one, Google no longer shows the &ldquo;this app has not been
              verified&rdquo; warning, and the connection is not limited to a small test group.
            </p>
            <p className="sec__p">
              The approval covers the Google connections CellCog runs on its own Google client:
              Gmail and Google Analytics. It is renewed every year.
            </p>
          </section>

          <section className="sec__block" aria-labelledby="sec-casa">
            {TRUST_ROW.casa.badgeSrc ? (
              <img
                className="sec__badge"
                src={TRUST_ROW.casa.badgeSrc}
                alt={TRUST_ROW.casa.badgeAlt}
                loading="lazy"
              />
            ) : null}
            <h2 id="sec-casa" className="sec__h2">{TRUST_ROW.casa.title}</h2>
            <p className="sec__p">
              Google requires applications with this level of access to complete a Cloud
              Application Security Assessment (CASA), run by the App Defense Alliance. CellCog
              completed the assessment at <strong>Tier 2</strong> with <strong>TAC Security</strong>,
              an App Defense Alliance authorized lab, in 2026. The assessment tests the
              application against the alliance&rsquo;s security requirements, which build on the
              OWASP Application Security Verification Standard.
            </p>
            <p className="sec__p">The assessment is repeated every year.</p>
          </section>

          <section className="sec__block" aria-labelledby="sec-data">
            <h2 id="sec-data" className="sec__h2">How we handle your data</h2>
            <ul className="sec__list">
              <li>
                <strong>Connections you control.</strong> Every connection you grant to an AI
                employee can be revoked at any time from your Connectors page. Credentials are
                stored encrypted.
              </li>
              <li>
                <strong>Deletion is complete.</strong> Deleting your account removes your
                workspace, chats, your AI employees&rsquo; memory and the analytics records
                tied to you.
              </li>
              <li>
                <strong>Separate identities per AI employee.</strong> Each AI employee browses
                with its own browser identity and logins, shared only if you choose to share them.
              </li>
              <li>
                <strong>Analytics that respect your choices.</strong> Our analytics are
                measurement only, configured to honor your preferences, including Global
                Privacy Control. You can change them any time under Cookie preferences in
                the footer.
              </li>
            </ul>
          </section>

          <section className="sec__block sec__block--links" aria-labelledby="sec-more">
            <h2 id="sec-more" className="sec__h2">Read more</h2>
            <ul className="sec__links">
              <li><Link to="/support/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/support/terms-of-service">Terms of Service</Link></li>
              <li><Link to="/connectors">Connectors and what each one can access</Link></li>
              <li><a href="/blog/casa-security-assessment/">The announcement on our blog</a></li>
              <li><Link to="/contact">Questions about security</Link></li>
            </ul>
            <p className="sec__stamp">Last updated September 16, 2026.</p>
          </section>
        </div>
      </div>
    </>
  );
};

export default SecurityPage;
